interface SubscriptionInfo {
  id: string;
  status: string;
  currentPeriodStart: number;
  currentPeriodEnd: number;
  cancelAtPeriodEnd: boolean;
  cancelAt: number | null;
  amount: number;
  currency: string;
  interval: string;
}

interface UpcomingInvoice {
  amountDue: number;
  nextPaymentAttempt: number | null;
  dueDate: number | null;
}

export interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  requiresPayment?: boolean;
  subscription?: SubscriptionInfo;
  upcomingInvoice?: UpcomingInvoice;
}

export async function checkSubscriptionStatus(customerId: string): Promise<SubscriptionStatus | null> {
  try {
    const response = await fetch('/.netlify/functions/check-subscription', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ customerId }),
    });

    if (!response.ok) {
      throw new Error('Failed to check subscription status');
    }

    return await response.json();
  } catch (error) {
    // Silently fail - subscription check not critical for app functionality
    return null;
  }
}

// Find customer by email (for recovering missing customer IDs)
export async function findCustomerByEmail(email: string): Promise<any> {
  try {
    const response = await fetch('/.netlify/functions/find-customer', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      throw new Error('Failed to find customer');
    }

    return await response.json();
  } catch (error) {
    // Silently fail - not critical
    return null;
  }
}

export async function openCustomerPortal(customerId: string): Promise<void> {
  try {
    const response = await fetch('/.netlify/functions/create-customer-portal', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        customerId,
        returnUrl: window.location.href
      })
    });

    if (!response.ok) {
      throw new Error('Failed to create customer portal session');
    }

    const { url } = await response.json();
    window.location.href = url;
  } catch (error) {
    alert('Unable to open billing portal. Please try again or contact support if the issue persists.');
  }
}

export function formatSubscriptionDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export function formatAmount(amount: number, currency: string = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

export function getSubscriptionStatusColor(status: string): string {
  switch (status) {
    case 'active':
    case 'trialing':
      return 'green';
    case 'past_due':
      return 'orange';
    case 'canceled':
    case 'unpaid':
      return 'red';
    default:
      return 'gray';
  }
}

export function getSubscriptionStatusText(status: string): string {
  switch (status) {
    case 'active':
      return '✓ Active';
    case 'trialing':
      return '🎁 Trial';
    case 'past_due':
      return '⚠️ Payment Failed';
    case 'canceled':
      return '❌ Canceled';
    case 'unpaid':
      return '❌ Unpaid';
    default:
      return status;
  }
}

// Store subscription info in premium status
export function updatePremiumStatusWithSubscription(
  customerId: string,
  hasActiveSubscription: boolean,
  subscription?: SubscriptionInfo
): void {
  const premiumStatus = {
    isPremium: hasActiveSubscription,
    purchaseDate: subscription?.currentPeriodStart 
      ? new Date(subscription.currentPeriodStart * 1000).toISOString()
      : new Date().toISOString(),
    stripeCustomerId: customerId,
    subscriptionId: subscription?.id,
    subscriptionStatus: subscription?.status,
    currentPeriodEnd: subscription?.currentPeriodEnd,
    cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd
  };

  localStorage.setItem('premium_status', JSON.stringify(premiumStatus));
}
